import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Pencil } from 'lucide-react';
import { listForms } from '@/lib/api/forms.js';
import { prettyLabel } from '@/lib/format.js';

export function FormsSettingsPage() {
  const formsQ = useQuery({
    queryKey: ['custom-forms'],
    queryFn: () => listForms({ active: true }),
  });

  const forms = formsQ.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-950">Custom Forms</h1>
          <p className="text-sm text-slate-500">
            Build intake checklists, waivers, and custom data collection forms.
          </p>
        </div>
        <Link
          to="/settings/forms/new"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
        >
          <Plus className="h-4 w-4" />
          New Form
        </Link>
      </div>

      {formsQ.isLoading ? (
        <div className="rounded-2xl bg-white p-6 text-slate-500 shadow-sm">Loading forms…</div>
      ) : formsQ.isError ? (
        <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-red-800">
          Could not load forms.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Trigger</th>
                <th className="px-4 py-3">Fields</th>
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => (
                <tr key={form.id} className="border-b border-slate-100">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-950">{form.name}</p>
                    {form.description ? (
                      <p className="text-xs text-slate-500">{form.description}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 capitalize">{prettyLabel(form.triggerType.replace(/_/g, ' '))}</td>
                  <td className="px-4 py-3">{form.fields.length}</td>
                  <td className="px-4 py-3 text-slate-500">
                    {new Date(form.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/settings/forms/${form.id}/edit`}
                      className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!forms.length ? (
            <p className="p-8 text-center text-slate-500">
              No forms yet.{' '}
              <Link to="/settings/forms/new" className="font-semibold text-primary hover:underline">
                Create your first form
              </Link>
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
